import { PencilIcon, TrashIcon } from 'lucide-react'
import type { MouseEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Badge } from '../Badge'

export interface SavedListItemProps {
  title: string
  initials: string
  /** Tailwind bg-* class for the avatar square */
  colorClass?: string
  /** Pre-formatted date string, e.g. "5/2/2026" */
  formattedDate?: string
  lastUpdatedLabel?: string
  /** When true, renders an inline rename input */
  isRenaming?: boolean
  onLoad: () => void
  onEditClick: (e: MouseEvent) => void
  onDeleteClick: (e: MouseEvent) => void
  onRenameConfirm: (name: string) => void
  onRenameCancel: () => void
  editLabel?: string
  deleteLabel?: string
  className?: string
}

export function SavedListItem({
  title,
  initials,
  colorClass = 'bg-ink',
  formattedDate,
  lastUpdatedLabel = 'Last updated',
  isRenaming = false,
  onLoad,
  onEditClick,
  onDeleteClick,
  onRenameConfirm,
  onRenameCancel,
  editLabel = 'Rename',
  deleteLabel = 'Delete',
  className = '',
}: SavedListItemProps) {
  const [renameVal, setRenameVal] = useState(title)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isRenaming) {
      setRenameVal(title)
      // focus after paint
      requestAnimationFrame(() => inputRef.current?.select())
    }
  }, [isRenaming, title])

  const confirmRename = () => onRenameConfirm(renameVal.trim() || title)

  return (
    <div
      className={`group flex items-center gap-3 p-2.5 bg-surface-card dark:bg-dark-card border-2 border-ink dark:border-dark-border rounded-[10px] shadow-[2px_2px_0_#111111] dark:shadow-[2px_2px_0_#000000] hover:shadow-[3px_3px_0_#111111] dark:hover:shadow-[3px_3px_0_#000000] hover:-translate-x-px hover:-translate-y-px transition-all duration-150 cursor-pointer ${className}`}
    >
      {/* Avatar */}
      <div onClick={isRenaming ? undefined : onLoad}>
        <Badge initials={initials} colorClass={colorClass} shape="square" />
      </div>

      {/* Title + date */}
      <div className="flex-1 min-w-0" onClick={isRenaming ? undefined : onLoad}>
        {isRenaming ? (
          <input
            ref={inputRef}
            value={renameVal}
            onChange={(e) => setRenameVal(e.target.value)}
            onBlur={confirmRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmRename()
              if (e.key === 'Escape') onRenameCancel()
            }}
            onClick={(e) => e.stopPropagation()}
            className="w-full font-extrabold text-sm text-ink dark:text-dark-ink bg-surface dark:bg-dark-bg border-2 border-cyan rounded-md px-2 py-0.5 outline-none ring-2 ring-cyan/30 transition-colors"
          />
        ) : (
          <div
            className="font-extrabold text-sm text-ink dark:text-dark-ink truncate"
            title={title}
          >
            {title}
          </div>
        )}
        {formattedDate && (
          <div className="text-[11px] font-bold text-ink-secondary dark:text-dark-ink2 mt-0.5">
            {lastUpdatedLabel} {formattedDate}
          </div>
        )}
      </div>

      {/* Edit + Delete */}
      <div className="flex gap-1 shrink-0">
        <button
          type="button"
          aria-label={editLabel}
          data-test="edit-saved-list-button"
          onClick={onEditClick}
          className="w-7 h-7 flex items-center justify-center bg-transparent border border-ink/20 dark:border-dark-border/40 rounded-md text-ink-secondary dark:text-dark-ink2 hover:border-cyan hover:text-cyan transition-colors duration-100"
        >
          <PencilIcon className="h-3 w-3" />
        </button>
        <button
          type="button"
          aria-label={deleteLabel}
          data-test="delete-saved-list-button"
          onClick={onDeleteClick}
          className="w-7 h-7 flex items-center justify-center bg-transparent border border-ink/20 dark:border-dark-border/40 rounded-md text-ink-secondary dark:text-dark-ink2 hover:border-con hover:text-con dark:hover:text-con-dark transition-colors duration-100"
        >
          <TrashIcon className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}
